import { useFormikContext } from 'formik'
import { useCallback } from 'react'

import ancillaryUtils from '@lib/ancillary'
import connectionUtils from '@lib/connection'
import { useTranslation } from '@lib/i18n'
import utils from '@lib/utils'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import { useSeatAncillaryContext } from '@pages/Checkout/SeatSelection/NewSeats/context/seatAncillary'
import { useSettings } from '@queries/settings'
import { useCheckout } from '@stores/checkout'

export function useSeatValidation(enabled: boolean, scheme: Partial<Seat.ByTripDirection<Seat.Train.Segment[]>>) {
  const { t } = useTranslation()
  const [{ seatSelection }] = useSettings()
  const { activeAncillaries } = useSeatAncillaryContext()
  const [{ outbound, inbound }] = useCheckout()
  const {
    values: { passengers },
  } = useFormikContext<CheckoutFormData>()

  return useCallback(
    (value: CheckoutFormData['seats']): string | undefined => {
      if (!enabled || (!seatSelection.mandatory && !activeAncillaries.length)) return

      function areSegmentSeatsChosen(seats?: Seat.Selected[]) {
        return seats?.length === passengers.length
      }

      function isSegmentValid(seats: Seat.Selected[] | undefined) {
        return !seats?.length || areSegmentSeatsChosen(seats)
      }

      function isConnectionValid(
        connection: Connection,
        connectionScheme: Seat.Train.Segment[] | undefined,
        seats: Seat.BySegment | undefined,
        forceSelection = false,
      ) {
        const validateSegment = forceSelection ? areSegmentSeatsChosen : isSegmentValid

        for (const segment of connection.segments) {
          const segmentScheme = connectionScheme?.find(segmentScheme =>
            connectionUtils.isSameSegment(segmentScheme, segment),
          )
          if (segmentScheme && !validateSegment(seats?.[segmentScheme.index])) return false
        }

        return true
      }

      function areRelevantConnectionSegmentsValid(
        activeAncillary: Ancillary.Item,
        connection: Connection,
        connectionScheme: Seat.Train.Segment[] | undefined,
        seats?: Seat.BySegment,
        forceSelection = false,
      ) {
        const validateSegment = forceSelection ? areSegmentSeatsChosen : isSegmentValid

        const relevantSegments = utils.array.compact(
          activeAncillary.segments?.map(({ id: segmentId }) =>
            connection.segments.find(segment => segment.id === segmentId),
          ) || [],
        )

        for (const relevantSegment of relevantSegments) {
          const segmentScheme = connectionScheme?.find(segmentScheme =>
            connectionUtils.isSameSegment(relevantSegment, segmentScheme),
          )
          if (segmentScheme && !validateSegment(seats?.[segmentScheme.index])) return false
        }

        return true
      }

      if (outbound && !isConnectionValid(outbound, scheme.outbound, value?.outbound, seatSelection.mandatory))
        return t('errors.mismatchSeats')
      if (inbound && !isConnectionValid(inbound, scheme.outbound, value?.outbound, seatSelection.mandatory))
        return t('errors.mismatchSeats')

      for (const activeAncillary of activeAncillaries) {
        const isMandatory = ancillaryUtils.isMandatory(activeAncillary)

        if (
          outbound &&
          !areRelevantConnectionSegmentsValid(activeAncillary, outbound, scheme.outbound, value?.outbound, isMandatory)
        ) {
          return t('errors.mismatchSeats')
        }

        if (
          inbound &&
          !areRelevantConnectionSegmentsValid(activeAncillary, inbound, scheme.inbound, value?.inbound, isMandatory)
        ) {
          return t('errors.mismatchSeats')
        }
      }
    },
    [activeAncillaries, enabled, inbound, outbound, passengers.length, scheme, seatSelection.mandatory, t],
  )
}
